import React from "react";
import { Box, Heading, Icon, Text, VStack } from "@chakra-ui/react";
import type { IconType } from "react-icons";

export interface EmptyStateProps {
  icon: IconType;
  title: string;
  description?: string;
  /** e.g. <Button variant="primary" size="sm">New ticket</Button> */
  action?: React.ReactNode;
  /** Vertical padding (Chakra space, default 12). */
  py?: number;
  /** Element for the title (default "h3"). Pass "p" when the empty state sits inside a section that already has a heading. */
  headingAs?: "h2" | "h3" | "h4" | "h5" | "p";
}

/**
 * Centred empty state with a static organic leaf blob behind the icon.
 * @example <EmptyState icon={FiInbox} title="No tickets yet" description="Raise your first ticket…" action={<Button variant="primary" size="sm">New ticket</Button>} />
 */
export const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, action, py = 12, headingAs = "h3" }) => (
  <VStack spacing={3} py={py} px={4} textAlign="center">
    <Box
      w="88px"
      h="88px"
      bg="leaf.100"
      borderRadius="42% 58% 55% 45% / 50% 40% 60% 50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      aria-hidden
    >
      <Icon as={icon} boxSize="36px" color="leaf.700" />
    </Box>
    <Heading as={headingAs} size="sm" mt={2}>
      {title}
    </Heading>
    {description && (
      <Text textStyle="body.sm" color="text.muted" maxW="36ch">
        {description}
      </Text>
    )}
    {action && <Box pt={2}>{action}</Box>}
  </VStack>
);
